import { Injectable } from '@angular/core';

import { Bet, BetSlip, Competition, SlipState, Sport } from '@bpos/v1/my-bets';
import { Fixture, FixtureType } from '@cds/betting-offer';
import { BatchRequest, FixtureRequest, FixtureState, MyBetsFixtureRequest, OfferMapping, ScoreboardMode } from '@cds/query-objects';
import { ScoreboardSlim } from '@cds/scoreboards/v1';
import { PlayerStats } from '@cds/statistics/player';
import { BatchResponse, BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { MyBetsVisualizationTrackingConfig } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { flatten, groupBy, keys, toString, uniq, uniqBy } from 'lodash-es';
import { Observable, map, of, switchMap } from 'rxjs';

import { MyBetsBatchResponse, MyBetsOfferApi } from '../../cds/my-bets-offer-api.service';
import { MyBetsFixtureFactory } from '../../event-model/mappers/my-bets-fixture.factory';
import { MyBetsEventModel } from '../models/my-bets-event.model';
import { MarketStatInfo } from '../models/my-bets-visualization-types';

export class EventInfo {
    fixtureId: string;
    marketId: number;
    fixtureName: string;
    sport: Sport;
    competition: Competition;
}

@Injectable({ providedIn: 'root' })
export class MyBetsFixtureDataService {
    constructor(
        private bettingOffer: BettingOfferApi,
        private myBetsOffer: MyBetsOfferApi,
        private mybetsVisualizationTrackingConfig: MyBetsVisualizationTrackingConfig,
        private myBetsFixtureFactory: MyBetsFixtureFactory,
    ) {}

    fetch(betslips: BetSlip[], isSettled: boolean): Observable<{ [id: string]: MyBetsEventModel }> {
        return this.mybetsVisualizationTrackingConfig.whenReady.pipe(
            switchMap(() => {
                if (this.mybetsVisualizationTrackingConfig.isEnabled) {
                    return this.getMyBetsFixtureList(betslips, isSettled);
                } else {
                    return this.getBatchFixtureList(betslips);
                }
            }),
        );
    }

    private getMyBetsFixtureList(betslips: BetSlip[], isSettled: boolean): Observable<{ [id: string]: MyBetsEventModel }> {
        const eventDetails = this.getEventDetails(betslips, isSettled);
        if (!eventDetails.length) {
            return of({});
        }

        const groupByEvents = groupBy(eventDetails, (e) => e.fixtureId);
        const batchRequest = keys(groupByEvents).map(
            (key) =>
                <BatchRequest<MyBetsFixtureRequest>>{
                    batchId: toString(key),
                    request: {
                        fixtureIds: toString(groupByEvents[key][0].fixtureId),
                        scoreboardMode: ScoreboardMode.Full,
                        marketIds: toString(groupByEvents[key].map((x) => x.marketId)),
                    },
                },
        );

        return this.myBetsOffer.getMyBetsFixtureList(batchRequest).pipe(
            map((response) => response || {}),
            map((response) => this.createEventStatModels(response, eventDetails)),
        );
    }

    private getBatchFixtureList(betslips: BetSlip[]): Observable<{ [id: string]: MyBetsEventModel }> {
        const eventIds = this.getEventIds(betslips);
        if (!eventIds.length) {
            return of({});
        }

        const batchRequest = eventIds.map(
            (eventId) =>
                <BatchRequest<FixtureRequest>>{
                    batchId: toString(eventId),
                    request: {
                        fixtureTypes: FixtureType.Standard,
                        state: FixtureState.Latest,
                        offerMapping: OfferMapping.None,
                        fixtureIds: toString(eventId),
                        scoreboardMode: ScoreboardMode.Full,
                    },
                },
        );

        return this.bettingOffer.getBatchFixtureList(batchRequest).pipe(
            map((response) => response || {}),
            map((response) => this.createEventModels(response)),
        );
    }

    private createEventStatModels(response: MyBetsBatchResponse, eventInfo: EventInfo[]): Record<string, MyBetsEventModel> {
        const result: Record<string, MyBetsEventModel> = {};

        for (const [key, value] of Object.entries(response)) {
            //TODO any index issue, probably a defect, please investigate value[0] never exists
            //@ts-ignore I have no idea how is this working for now, probably it's not. To be investigated
            const fixture = value[0];
            if (!fixture) {
                continue;
            }

            const filterFixture = eventInfo.find(
                (x) => x.fixtureId === fixture.id || (fixture.tv2RelatedFixtureId && x.fixtureId === fixture.tv2RelatedFixtureId),
            );

            if (filterFixture) {
                result[key] = this.myBetsFixtureFactory.createFromMyBetsFixture(fixture, filterFixture);
            }
        }

        const uniqueIds = Array.from(new Set(Object.values(result).map((model) => model.id)));

        for (const id of uniqueIds) {
            const matchingModels = Object.values(result).filter((model) => model.id === id);

            if (matchingModels.length > 1) {
                const combinedMarkets = this.getCombinedMarkets(matchingModels);
                const combinedPlayerStats = this.getCombinedPlayerStats(matchingModels);
                matchingModels.forEach((model) => {
                    model.marketStatInfo = combinedMarkets;
                    model.playerStats = combinedPlayerStats;
                });
            }
        }

        return result;
    }

    private getCombinedMarkets(matchingModels: MyBetsEventModel[]): MarketStatInfo[] {
        return matchingModels.reduce<MarketStatInfo[]>((acc, model) => {
            const currentMarkets = model.marketStatInfo || [];

            return acc.concat(currentMarkets);
        }, []);
    }

    private getCombinedPlayerStats(matchingModels: MyBetsEventModel[]): PlayerStats[] {
        return matchingModels.reduce<PlayerStats[]>((acc, model) => {
            const currentStats = model.playerStats || [];
            currentStats.forEach((stat) => {
                if (!acc.find((existingStat) => existingStat.id === stat.id)) {
                    acc.push(stat);
                }
            });

            return acc;
        }, []);
    }

    private createEventModels(cdsResponse: BatchResponse): { [key: string]: MyBetsEventModel } {
        const result: { [key: string]: MyBetsEventModel } = {};
        for (const [key, value] of Object.entries(cdsResponse)) {
            const fixture = value.fixtures[0];
            if (!fixture) {
                continue;
            }
            result[key] = this.toEventModel(fixture);
        }

        return result;
    }

    private toEventModel(fixture: Fixture): MyBetsEventModel {
        const event = this.myBetsFixtureFactory.createEventFromFixture(fixture);
        event.fixtureScoreboard = fixture.scoreboard as ScoreboardSlim;

        return event;
    }

    private getEventIds(betslips: BetSlip[]): string[] {
        const bets = flatten(betslips.filter((b) => b.state === SlipState.Open).map((betslip) => betslip.bets));

        return uniq(
            bets.map(({ fixture, optionBetDetails }) => {
                if (optionBetDetails) {
                    return optionBetDetails.betBuilderData?.tv1BetBuilderInfo?.event?.id.toString() ?? fixture.compoundId;
                }

                return fixture.id.toString();
            }),
        );
    }

    private getEventDetails(betSlips: BetSlip[], isSettled: boolean): EventInfo[] {
        const relevantBets = isSettled ? this.filterFlattenSettledBets(betSlips) : this.flattenBets(betSlips);

        return this.buildEventInfoList(relevantBets);
    }

    private flattenBets(betSlips: BetSlip[]): Bet[] {
        return betSlips.flatMap((betSlip) => betSlip.bets);
    }

    private filterFlattenSettledBets(betSlips: BetSlip[]): Bet[] {
        const currentDateTime = DateProviderService.getNow();

        return betSlips
            .flatMap((betSlip) => betSlip.bets)
            .filter((bet) => {
                const eventDate = new Date(bet.fixture.date!);
                if (!eventDate || isNaN(eventDate.getTime())) {
                    return false;
                }

                const cutoffDateTime = DateProviderService.addHours(new Date(new Date(eventDate.toUTCString()).getTime()), 12);

                return cutoffDateTime > currentDateTime;
            });
    }

    private buildEventInfoList(bets: Bet[]): EventInfo[] {
        return uniqBy(
            bets.map(({ fixture, optionBetDetails, market, sport, competition }) => {
                let fixtureId: string;
                if (optionBetDetails) {
                    fixtureId = optionBetDetails.betBuilderData?.tv1BetBuilderInfo?.event?.id.toString() ?? fixture.compoundId;
                    competition = {
                        id: optionBetDetails.betBuilderData?.tv1BetBuilderInfo?.league?.id ?? competition?.id,
                        name: optionBetDetails.betBuilderData?.tv1BetBuilderInfo?.league?.name ?? competition?.name,
                    };
                } else {
                    fixtureId = fixture.id.toString();
                }
                const marketId = market.id ?? 0;

                return { fixtureId, marketId, fixtureName: fixture.name, sport, competition };
            }),
            (myBets) => `${myBets.fixtureId} - ${myBets.marketId}`,
        );
    }
}
